// packages/frontend/src/features/cook-sessions/CompletionModal.tsx
//
// Spec §6.9 / §7.4 step 9: "Finish" modal captures cook rating + optional cook
// note + dish rating (now or later). Calls onComplete with the collected values.
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog.js";
import { Button } from "../../ui/button.js";
import { Textarea } from "../../ui/textarea.js";
import Rating from "../recipes/Rating.js";
import type { RatingValue } from "../recipes/Rating.js";

type DishMode = "later" | "now";

export interface CompletionModalProps {
  onComplete: (body: {
    cookRating: number | null;
    cookNote: string | null;
    dishRating: number | null;
    dishNote?: string | null;
  }) => void;
  onCancel: () => void;
}

export function CompletionModal({ onComplete, onCancel }: CompletionModalProps) {
  const [cookRating, setCookRating] = useState<RatingValue>(null);
  const [cookNote, setCookNote] = useState("");
  const [dishMode, setDishMode] = useState<DishMode>("later");
  const [dishRating, setDishRating] = useState<RatingValue>(null);
  const [dishNote, setDishNote] = useState("");

  function handleFinish() {
    onComplete({
      cookRating,
      cookNote: cookNote.trim() || null,
      dishRating: dishMode === "now" ? dishRating : null,
      ...(dishMode === "now" ? { dishNote: dishNote.trim() || null } : {}),
    });
  }

  return (
    <Dialog open>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Finish cooking</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {/* Cook rating */}
          <section aria-label="How did the cook go?">
            <p className="text-sm text-muted-foreground mb-2">How did the cook go?</p>
            <Rating value={cookRating} onChange={setCookRating} />
          </section>

          {/* Cook note */}
          <section aria-label="Cook note">
            <p className="text-sm text-muted-foreground mb-2">
              Cook note <span className="text-xs">(optional)</span>
            </p>
            <Textarea
              placeholder="Any notes about this cook…"
              maxLength={2000}
              value={cookNote}
              onChange={(e) => setCookNote(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </section>

          {/* Dish rating — now or later */}
          <section aria-label="Rate the dish">
            <p className="text-sm text-muted-foreground mb-2">Rate the dish</p>
            <div className="flex gap-2 mb-3">
              <Button
                type="button"
                variant={dishMode === "later" ? "default" : "outline"}
                size="sm"
                className="min-h-[48px]"
                onClick={() => setDishMode("later")}
              >
                Rate later
              </Button>
              <Button
                type="button"
                variant={dishMode === "now" ? "default" : "outline"}
                size="sm"
                className="min-h-[48px]"
                onClick={() => setDishMode("now")}
              >
                Rate now
              </Button>
            </div>
            {dishMode === "now" && (
              <>
                <div data-testid="dish-rating">
                  <Rating value={dishRating} onChange={setDishRating} />
                </div>
                <div className="mt-3">
                  <p className="text-sm text-muted-foreground mb-2">
                    Dish note <span className="text-xs">(optional)</span>
                  </p>
                  <Textarea
                    placeholder="Any notes about this dish…"
                    maxLength={2000}
                    value={dishNote}
                    onChange={(e) => setDishNote(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                </div>
              </>
            )}
          </section>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleFinish}>
            Finish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
